import React, { useState, useRef } from 'react'
import styled from 'styled-components'

const ResizeHandle = styled.div<{ $isHovered: boolean }>`
  position: absolute;
  left: -3px;
  top: 0;
  bottom: 0;
  width: 6px;
  cursor: col-resize;
  background: ${({ $isHovered }) => ($isHovered ? 'var(--primary, #0294df)' : 'transparent')};
  opacity: ${({ $isHovered }) => ($isHovered ? 0.3 : 1)};
  transition:
    background 0.2s,
    opacity 0.2s;
  z-index: 10;

  /* Hide resize handle on mobile */
  @media (max-width: 768px) {
    display: none;
  }
`

interface MapResizeHandleProps {
  onResize?: (width: number) => void
  minWidth?: number
  maxWidth?: number
}

export const MapResizeHandle: React.FC<MapResizeHandleProps> = ({
  onResize,
  minWidth = 300,
  maxWidth = 1000,
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const [isResizing, setIsResizing] = useState(false)
  const startXRef = useRef(0)
  const startWidthRef = useRef(0)

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
    startXRef.current = e.clientX

    // Get the current width of the map container
    const mapContainer = e.currentTarget.parentElement
    if (mapContainer) {
      startWidthRef.current = mapContainer.getBoundingClientRect().width
    }

    document.body.style.cursor = 'col-resize'
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizing) return

    const deltaX = e.clientX - startXRef.current
    const newWidth = Math.max(minWidth, Math.min(maxWidth, startWidthRef.current + deltaX))

    onResize?.(newWidth)
  }

  const handleMouseUp = () => {
    setIsResizing(false)
    document.body.style.cursor = ''
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
  }

  return (
    <ResizeHandle
      $isHovered={isHovered || isResizing}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseDown={handleMouseDown}
    />
  )
}
